import React from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/lib/images";

/**
 * Enterprise SEO component.
 * Props:
 * - title, description, keywords, image, url (canonical)
 * - type: 'website' | 'article' | 'product'
 * - schema: raw JSON-LD object (optional) — merged with defaults
 * - noIndex: boolean
 * - breadcrumbs: [{name, url}] for BreadcrumbList schema
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  schema,
  noIndex = false,
  breadcrumbs,
  article,
  product,
  faqs,
}) => {
  const { settings } = useSettings();
  const { lang } = useLanguage();
  const s = settings || {};

  const finalTitle = title ? `${title} | ${s.site_name || "Mandala"}` : s.seo_title;
  const finalDesc = description || s.seo_description;
  const finalKeywords = keywords || s.seo_keywords;
  const finalImage = image || s.logo || IMAGES.factory;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const finalUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const jsonLd = [];

  // Organization schema
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: s.site_name || "PT Mandala Cahaya Sentosa",
    url: origin,
    logo: finalImage,
    email: s.email,
    telephone: s.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: s.address,
      addressCountry: "ID",
    },
    sameAs: Object.values(s.social || {}).filter(Boolean),
  });

  // WebSite + SearchAction
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: s.site_name,
    url: origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${origin}/produk?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });

  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    });
  }

  if (article) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt || finalDesc,
      image: article.thumbnail || finalImage,
      datePublished: article.created_at,
      dateModified: article.created_at,
      author: { "@type": "Person", name: article.author || "Admin" },
      publisher: {
        "@type": "Organization",
        name: s.site_name,
        logo: { "@type": "ImageObject", url: finalImage },
      },
      mainEntityOfPage: finalUrl,
    });
  }

  if (product) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: product.image,
      category: product.category,
      brand: { "@type": "Brand", name: s.site_name || "Mandala" },
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "IDR",
        seller: { "@type": "Organization", name: s.site_name },
      },
    });
  }

  if (faqs && faqs.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  if (schema) jsonLd.push(schema);

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="keywords" content={finalKeywords} />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={finalUrl} />

      {/* OG */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={s.site_name} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "id_ID"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
